/** 분석에 필요한 최소 녹음 길이 (초) — UI 안내와 동일 */
export const MIN_ANALYSIS_AUDIO_SECONDS = 10;

/** 실제로 소리가 들리는 구간 최소 길이 (초) */
const MIN_ACTIVE_AUDIO_SECONDS = 4;

/** 100ms 윈도우 RMS가 이 값 이상이면 '소리 있음'으로 간주 */
const ACTIVE_WINDOW_RMS_THRESHOLD = 0.0012;

/** 최소 한 구간이라도 이 peak RMS 이상이어야 분석 가능 */
const MIN_PEAK_RMS = 0.006;

export interface AudioUsabilityMetrics {
  durationSeconds: number;
  activeDurationSeconds: number;
  peakRms: number;
}

export function getReliableDurationSeconds(audioBuffer: AudioBuffer): number {
  const fromMeta = audioBuffer.duration;
  const fromSamples = audioBuffer.length / audioBuffer.sampleRate;

  if (Number.isFinite(fromMeta) && fromMeta > 0) {
    return Math.max(fromMeta, fromSamples);
  }

  return fromSamples;
}

export function measureAudioUsability(
  audioBuffer: AudioBuffer
): AudioUsabilityMetrics {
  const channelData = audioBuffer.getChannelData(0);
  const sampleRate = audioBuffer.sampleRate;
  const windowSize = Math.max(1, Math.floor(sampleRate * 0.1)); // 100ms

  let peakRms = 0;
  let activeWindows = 0;

  for (let i = 0; i < channelData.length; i += windowSize) {
    const end = Math.min(i + windowSize, channelData.length);
    let sumSq = 0;
    for (let j = i; j < end; j++) {
      const sample = channelData[j];
      sumSq += sample * sample;
    }

    const rms = Math.sqrt(sumSq / Math.max(1, end - i));
    if (rms > peakRms) peakRms = rms;
    if (rms >= ACTIVE_WINDOW_RMS_THRESHOLD) activeWindows++;
  }

  return {
    durationSeconds: getReliableDurationSeconds(audioBuffer),
    activeDurationSeconds: (activeWindows * windowSize) / sampleRate,
    peakRms,
  };
}

export function validateAudioUsability(
  audioBuffer: AudioBuffer
): { ok: true; metrics: AudioUsabilityMetrics } | { ok: false; error: string } {
  const metrics = measureAudioUsability(audioBuffer);

  if (metrics.durationSeconds < MIN_ANALYSIS_AUDIO_SECONDS) {
    const rounded = Math.max(1, Math.round(metrics.durationSeconds));
    return {
      ok: false,
      error: `녹음이 ${rounded}초로 짧아요. 분석하려면 최소 ${MIN_ANALYSIS_AUDIO_SECONDS}초 이상 녹음해 주세요.`,
    };
  }

  if (metrics.activeDurationSeconds < MIN_ACTIVE_AUDIO_SECONDS) {
    return {
      ok: false,
      error:
        "무음 구간이 너무 많거나 소리가 거의 들리지 않아요. 마이크에 가까이서 또렷하게 10초 이상 다시 녹음해 주세요.",
    };
  }

  if (metrics.peakRms < MIN_PEAK_RMS) {
    return {
      ok: false,
      error:
        "녹음 볼륨이 너무 작아요. 마이크 입력을 키우거나 조금 더 크게 불러서 다시 녹음해 주세요.",
    };
  }

  return { ok: true, metrics };
}

export async function decodeAndValidateAudioFile(
  file: File
): Promise<
  | { ok: true; audioBuffer: AudioBuffer; metrics: AudioUsabilityMetrics }
  | { ok: false; error: string }
> {
  if (typeof window === "undefined") {
    return { ok: false, error: "오디오 검증은 브라우저에서만 가능합니다." };
  }

  const AudioContextCtor =
    window.AudioContext ||
    (window as Window & { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;

  if (!AudioContextCtor) {
    return {
      ok: false,
      error: "이 브라우저는 오디오 분석을 지원하지 않습니다.",
    };
  }

  try {
    const arrayBuffer = await file.arrayBuffer();
    const audioContext = new AudioContextCtor();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0));
    await audioContext.close().catch(() => undefined);

    const validation = validateAudioUsability(audioBuffer);
    if (!validation.ok) {
      return validation;
    }

    return {
      ok: true,
      audioBuffer,
      metrics: validation.metrics,
    };
  } catch {
    return {
      ok: false,
      error:
        "오디오 파일을 읽을 수 없어요. 다른 형식으로 다시 녹음하거나 업로드해 주세요.",
    };
  }
}
